import { faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState, useTransition } from "react";
import { Container, Dropdown, DropdownButton, Nav } from "react-bootstrap";
import Navbar from "react-bootstrap/Navbar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import brandLogo from "../../../assets/images/costeo_logo.png";
import { checkPermission } from "../../../constant/permissions";
import { BUILD_TIME, BUILD_VERSION } from '../../../constants/buildInfo';
import { baseApi } from "../../../store/services/api";
import { logout } from "../../../store/slices/authSlice";
import { isGlobalAdminRole } from "../../../utils/isGlobalAdmin";
import { getFormattedDate } from "../../../utils/method";
import "./brand-header.scss";
import { languages } from "./constant";
import { navBarItems } from "./navBarItems";

function BrandHeaderAtom() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const [isPending, startTransition] = useTransition();

	// Get authentication state from Redux with fallback values to prevent suspension
	const authState = useSelector((state) => state.auth) || {};
	const { isAuthenticated = false, user = null } = authState;

	// Navigation state
	const [activeMenuItem, setActiveMenuItem] = useState(localStorage.getItem("selectedMenu"));
	const permissions = useMemo(() => user?.permissions || [], [user?.permissions]);
	const [showSubmenu, setShowSubmenu] = useState({});
	const [navItems, setNavItems] = useState([]);
	const [submenuTimeout, setSubmenuTimeout] = useState(null);

	// Memoize hasPermission to avoid recreating it on every render
	const hasPermission = useMemo(
		() => (area, accessType) =>
			checkPermission(permissions, area, accessType),
		[permissions],
	);

	// Handle menu item click with proper navigation
	const onMenuItemClick = useCallback(
		(menuKey, url) => {
			if (url) {
				localStorage.setItem("selectedMenu", menuKey);
				setActiveMenuItem(menuKey);
				startTransition(() => {
					navigate(`/${url}`);
				});
			} else {
				// For parent menu items without URLs, just toggle the submenu
				setShowSubmenu((prev) => {
					const newState = { ...prev };
					newState[menuKey] = !newState[menuKey];
					return newState;
				});
			}
		},
		[navigate, startTransition],
	);

	// Mouse event handlers for submenu with delay
	const handleMouseEnter = useCallback((key) => {
		// Clear any pending timeout
		if (submenuTimeout) {
			clearTimeout(submenuTimeout);
			setSubmenuTimeout(null);
		}
		setShowSubmenu((prev) => ({ ...prev, [key]: true }));
	}, [submenuTimeout]);

	const handleMouseLeave = useCallback((key) => {
		// Add delay before hiding to allow smooth movement to submenu
		const timeout = setTimeout(() => {
			setShowSubmenu((prev) => ({ ...prev, [key]: false }));
		}, 300); // 300ms delay
		setSubmenuTimeout(timeout);
	}, []);

	// Get display label for parent menu with selected submenu
	const getMenuLabel = useCallback(
		(navBar) => {
			if (
				navBar.submenu &&
				activeMenuItem &&
				activeMenuItem.startsWith(navBar.key + "-")
			) {
				const activeSubItem = navBar.submenu.find(
					(item) => item.key === activeMenuItem,
				);
				if (activeSubItem) {
					return (
						<>
							<span className="main-label">
								{t(navBar.label)}
							</span>
							<span className="submenu-label selected">
								{t(activeSubItem.label)}
							</span>
						</>
					);
				}
			}
			return t(navBar.label);
		},
		[activeMenuItem, t],
	);

	// Filter navigation items based on permissions
	useEffect(() => {
		// Projects is shown to every authenticated user. Permissions may be
		// empty for project-local users (no global role), so don't bail on that.
		if (!isAuthenticated) return;

		const isGlobalAdmin = isGlobalAdminRole(user?.role);

		// A menu entry is visible when it has no gate, or the user is a global
		// admin, or the user holds the entry's required permission. Admin-only
		// entries require a global-admin role.
		const isEntryVisible = (entry) => {
			if (entry.adminOnly) return isGlobalAdmin;
			if (entry.requiredPermission) {
				return (
					isGlobalAdmin ||
					hasPermission(
						entry.requiredPermission.area,
						entry.requiredPermission.type,
					)
				);
			}
			return true;
		};

		const filteredItems = navBarItems
			.map((navItem) => {
				if (!navItem.submenu) return navItem;
				return {
					...navItem,
					submenu: navItem.submenu.filter(isEntryVisible),
				};
			})
			.filter((navItem) =>
				navItem.submenu
					? navItem.submenu.length > 0
					: isEntryVisible(navItem),
			);

		setNavItems(filteredItems);
	}, [hasPermission, isAuthenticated, user?.role]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (submenuTimeout) {
				clearTimeout(submenuTimeout);
			}
		};
	}, [submenuTimeout]);

	// Render menu items
	const renderMenuItem = useCallback(
		(navBar) => {
			const isActive =
				activeMenuItem === navBar.key ||
				activeMenuItem?.startsWith(navBar.key + "-");

			if (navBar.submenu) {
				return (
					<div
						key={navBar.key}
						className={`nav-item-with-submenu ${isActive ? "active-parent" : ""}`}
						onMouseEnter={() => handleMouseEnter(navBar.key)}
						onMouseLeave={() => handleMouseLeave(navBar.key)}
					>
						<Nav.Link
							onClick={() =>
								onMenuItemClick(
									navBar.key,
									navBar.submenu.length > 0
										? null
										: navBar.url,
								)
							}
							className={isActive ? "active-menu" : ""}
						>
							{navBar.icon} {getMenuLabel(navBar)}
						</Nav.Link>
						{showSubmenu[navBar.key] && (
							<div className="submenu">
								{navBar.submenu.map((subItem) => (
									<Nav.Link
										key={subItem.key}
										onClick={() =>
											onMenuItemClick(
												subItem.key,
												subItem.url,
											)
										}
										className={[
											"submenu-item",
											activeMenuItem === subItem.key
												? "active-menu"
												: "",
										]
											.filter(Boolean)
											.join(" ")}
										title={t(subItem.label)}
									>
										<span
											className="submenu-item__icon"
											aria-hidden="true"
										>
											{subItem.icon}
										</span>
										<span className="submenu-item__label">
											{t(subItem.label)}
										</span>
									</Nav.Link>
								))}
							</div>
						)}
					</div>
				);
			}

			return (
				<Nav.Link
					onClick={() => onMenuItemClick(navBar.key, navBar.url)}
					key={navBar.key}
					className={isActive ? "active-menu" : ""}
					title={t(navBar.label)}
				>
					{navBar.icon} {t(navBar.label)}
				</Nav.Link>
			);
		},
		[
			activeMenuItem,
			getMenuLabel,
			handleMouseEnter,
			handleMouseLeave,
			onMenuItemClick,
			showSubmenu,
			t,
		],
	);

	// Clear all app-specific local storage items
	const clearLocalStorageItems = useCallback(() => {
		const itemsToClear = [
			"selectedMenu",
		];

		itemsToClear.forEach((item) => {
			if (item === "selectedMenu") {
				localStorage.setItem(item, "");
				setActiveMenuItem("");
			} else {
				localStorage.setItem(item, JSON.stringify([]));
			}
		});
	}, []);

	const onLogout = useCallback(() => {
		if (isLoggingOut) return; // Prevent multiple clicks

		setIsLoggingOut(true);

		// Use startTransition for the logout process
		startTransition(() => {
			// Use Redux logout action instead of AuthService directly
			dispatch(logout())
				.then(() => {
					clearLocalStorageItems();
					// Clear all RTK Query cache so the next login fetches fresh data.
					dispatch(baseApi.util.resetApiState());
					navigate("/login");
				})
				.catch((error) => {
					console.error("Error during logout:", error);
					dispatch(baseApi.util.resetApiState());
					navigate("/login");
				})
				.finally(() => {
					setIsLoggingOut(false);
				});
		});
	}, [dispatch, navigate, clearLocalStorageItems, isLoggingOut, startTransition]);

	// Use startTransition for language changes
	const handleSelect = useCallback((key) => {
		if (key !== i18n.language) {
			startTransition(() => {
				const newLang = key === "en" ? "EN" : "DE";
				setSelectedLanguage(newLang);
				i18n.changeLanguage(key);
			});
		}
	}, [i18n, startTransition]);

	const [selectedLanguage, setSelectedLanguage] = useState(
		i18n.language === "en" ? "EN" : "DE",
	);

	return (
		<Navbar className="p-0">
			<Container fluid>
				<Navbar.Brand className="p-0">
					<div
						className="navbar-brand-logo"
						role="button"
						onClick={() => {
							if (isAuthenticated) {
								// Set Projects as active when clicking logo to go home
								localStorage.setItem("selectedMenu", "projects");
								setActiveMenuItem("projects");
								startTransition(() => {
									navigate("/");
								});
							}
						}}
					>
						<img src={brandLogo} alt="costeo" />
						<Navbar.Text className="version-info">
							v{BUILD_VERSION} ({getFormattedDate(BUILD_TIME, true)})
						</Navbar.Text>
					</div>
				</Navbar.Brand>
				{/* Navigation items in the center */}
				{isAuthenticated && (
					<div className="navbar-center-wrapper">
						<Nav className="navbar-navigation">
							{navItems.map(renderMenuItem)}
						</Nav>
					</div>
				)}
				<Navbar.Collapse className="navbar-right-panel">
					<div className="mode-language-container">
						<DropdownButton
							id="language-selector-btn"
							title={selectedLanguage || languages[0].value}
							onSelect={handleSelect}
							className="language-selector"
							disabled={isPending}
						>
							{languages.map((language) => (
								<Dropdown.Item
									key={language.key}
									eventKey={language.key}
								>
									{language.value}
								</Dropdown.Item>
							))}
						</DropdownButton>
					</div>
					{isAuthenticated && (
						<Navbar.Text className="mb-5 d-flex brand-header-user-section">
							<div className="d-flex">
								<p className="text-primary mb-0 brand-header-icon">
									<FontAwesomeIcon icon={faUser} />
								</p>
								<p
									role="button"
									className="brand-header-username text-primary px-2 mb-0"
									onClick={() => {
										localStorage.setItem(
											"selectedMenu",
											"resetPassword",
										);
										setActiveMenuItem("resetPassword");
										startTransition(() => {
											navigate("/profile");
										});
									}}
								>
									{user?.name || user?.firstName || 'User'}
								</p>
							</div>
							<strong
								className={`brand-header-logout font-weight-bold text-primary ml-10 ${isLoggingOut || isPending ? "disabled-link" : ""
									}`}
								onClick={onLogout}
								role="button"
							>
								{isLoggingOut
									? t("auth.logging_out")
									: t("auth.logout")}
							</strong>
						</Navbar.Text>
					)}
				</Navbar.Collapse>
			</Container>
		</Navbar>
	);
}

export default BrandHeaderAtom;
